<template>
  <div class="bg-white rounded-lg p-6 border-[1px] border-gray-200">
    <div class="flex items-center">
      <div class="flex items-center gap-2 w-64">
        <h3 class="font-medium">Fellow's Expedition</h3>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-gray-600">{{ currentExpedition?.name }}</span>
        <Button
          variant="link"
          class="text-cosmicPurple hover:text-cosmicPurple/80 px-1"
          @click="isExpeditionSwitchOpen = !isExpeditionSwitchOpen"
        >
          (switch)
        </Button>
      </div>
    </div>

    <!-- Switch Expedition Section -->
    <div v-if="isExpeditionSwitchOpen" class="mt-4 space-y-4">
      <div class="space-y-2">
        <p class="text-sm text-gray-600">Select New Expedition</p>
        <Select v-model="selectedExpeditionId">
          <SelectTrigger class="w-full">
            <SelectValue placeholder="Choose an expedition..." />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                v-for="expedition in filteredExpeditions"
                :key="expedition.id"
                :value="expedition.id.toString()"
              >
                {{ expedition.name }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div class="flex justify-end">
        <Button
          :disabled="!selectedExpeditionId || isSubmitting"
          @click="handleSubmit"
          class="bg-cosmicPurple text-white hover:bg-cosmicPurple/80 font-normal"
        >
          <Loader2 v-if="isSubmitting" class="mr-2 h-4 w-4 animate-spin" />
          Switch Expedition
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-vue-next'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { API_ROUTES } from '@/const/apiRoutes'
import { useToast } from 'primevue/usetoast'
import { useExpeditionsStore } from '@/stores/expeditions'
import api from '@/utils/api'

const props = defineProps<{
  userId: string
}>()

const toast = useToast()
const expeditionsStore = useExpeditionsStore()

// State
const isSubmitting = ref(false)
const selectedExpeditionId = ref<string | null>(null)
const isExpeditionSwitchOpen = ref(false)

// Computed
const currentExpedition = computed(() => {
  return expeditionsStore.expeditions.find((expedition) =>
    expedition.adventurers.some((adv: any) => adv.id.toString() === props.userId)
  )
})

const filteredExpeditions = computed(() => {
  if (!currentExpedition.value) return expeditionsStore.expeditions
  return expeditionsStore.expeditions.filter(
    (expedition) => 
      expedition.path === currentExpedition.value.path && 
      expedition.id !== currentExpedition.value.id
  )
})

// Methods
const handleSubmit = async () => {
  if (!selectedExpeditionId.value || !currentExpedition.value) return

  isSubmitting.value = true
  try {
    await api.post(API_ROUTES.SWITCH_USER_EXPEDITION(currentExpedition.value.id.toString()), {
      user_id: props.userId,
      new_expedition_id: selectedExpeditionId.value
    })
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'User switched expedition successfully',
      life: 3000
    })
    await expeditionsStore.fetchExpeditions()
  } catch (err: any) {
    console.error('Failed to switch expedition:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err.response?.data?.message || 'Failed to switch expedition',
      life: 3000
    })
  } finally {
    isSubmitting.value = false
  }
}
</script> 